import React, { useState } from 'react';
import { FaWater, FaMapMarkerAlt, FaSearch, FaWind, FaThumbsUp, FaThumbsDown, FaQuestion, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaCompass } from 'react-icons/fa';
import { getSurfConditions, geocodeLocation } from '../../api/client';

const SurfingSwellDecoder = () => {
  const [loading, setLoading] = useState(false);
  const [surfData, setSurfData] = useState(null);
  const [error, setError] = useState('');

  // Search inputs
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [spotName, setSpotName] = useState('');

  const handleSearch = async () => {
    if (!searchCity.trim()) {
      setError('Please enter a beach or surf spot');
      return;
    }

    setLoading(true);
    setError('');
    setSurfData(null);

    try {
      // First geocode the location
      const geoData = await geocodeLocation(searchCity.trim(), searchState.trim() || null, searchCountry.trim() || null);

      if (!geoData.results || geoData.results.length === 0) {
        setError('Location not found. Try a different search term.');
        setLoading(false);
        return;
      }

      const location = geoData.results[0];
      const name = spotName.trim() || `${searchCity}${searchState ? ', ' + searchState : ''}`;

      // Get surf conditions
      const data = await getSurfConditions(location.lat, location.lng, name);
      setSurfData(data);
    } catch (err) {
      console.error('Error fetching surf conditions:', err);
      setError('Failed to fetch surf conditions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getVerdictColor = (verdict) => {
    if (verdict.includes('Yes')) return 'bg-emerald-500';
    if (verdict.includes('Maybe')) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getVerdictIcon = (verdict) => {
    if (verdict.includes('Yes')) return <FaThumbsUp className="text-4xl" />;
    if (verdict.includes('Maybe')) return <FaQuestion className="text-4xl" />;
    return <FaThumbsDown className="text-4xl" />;
  };

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <div className="bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {/* Wave pattern */}
          <svg className="absolute bottom-0 left-0 right-0 opacity-20" viewBox="0 0 1440 200">
            <path fill="white" d="M0,96L48,106.7C96,117,192,139,288,138.7C384,139,480,117,576,112C672,107,768,117,864,138.7C960,160,1056,192,1152,186.7C1248,181,1344,139,1392,117.3L1440,96L1440,200L1392,200C1344,200,1248,200,1152,200C1056,200,960,200,864,200C768,200,672,200,576,200C480,200,384,200,288,200C192,200,96,200,48,200L0,200Z" />
          </svg>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FaWater className="text-2xl text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Surfing Swell Decoder</h2>
              <p className="text-white/70">Should you go surfing today?</p>
            </div>
          </div>

          <p className="text-white/80 mt-4">
            Enter a surf spot to decode complex buoy data into a simple answer.
            We analyze swell, wind, and tides to tell you if it's worth paddling out.
          </p>
        </div>
      </div>

      {/* Search Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
        <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
          <FaMapMarkerAlt className="text-teal-500" />
          Find Your Surf Spot
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Beach / Surf Spot *
            </label>
            <input
              type="text"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              placeholder="e.g., Gold Coast, Bali"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              State / Region
            </label>
            <input
              type="text"
              value={searchState}
              onChange={(e) => setSearchState(e.target.value)}
              placeholder="e.g., Queensland, Hawaii"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Country
            </label>
            <input
              type="text"
              value={searchCountry}
              onChange={(e) => setSearchCountry(e.target.value)}
              placeholder="e.g., Australia, Indonesia"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Spot Name (optional)
            </label>
            <input
              type="text"
              value={spotName}
              onChange={(e) => setSpotName(e.target.value)}
              placeholder="e.g., Snapper Rocks"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <button
          onClick={handleSearch}
          disabled={loading}
          className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <FaSearch />
          )}
          {loading ? 'Decoding Swell...' : 'Decode Swell'}
        </button>

        {error && (
          <p className="text-red-600 text-sm mt-3">{error}</p>
        )}
      </div>

      {/* Results */}
      {surfData && (
        <div className="space-y-6 animate-fade-in">
          {/* Main Verdict Card */}
          <div className={`${getVerdictColor(surfData.verdict)} rounded-2xl p-8 text-white text-center`}>
            <div className="flex items-center justify-center gap-4 mb-4">
              {getVerdictIcon(surfData.verdict)}
              <span className="text-6xl">{surfData.verdict_emoji}</span>
            </div>
            <h3 className="text-3xl font-bold mb-2">{surfData.verdict}</h3>
            <p className="text-white/90 text-lg">{surfData.location_name}</p>
            <div className="mt-4 inline-block bg-white/20 rounded-xl px-6 py-2">
              <span className="text-2xl font-bold">{surfData.confidence_score}/10</span>
              <span className="text-white/80 ml-2">Confidence</span>
            </div>
          </div>

          {/* Wave Height Highlight */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100 text-center">
            <p className="text-neutral-500 text-sm mb-2">Expected Wave Height</p>
            <div className="text-5xl font-bold text-teal-600">{surfData.wave_height_estimate_ft}</div>
            <p className="text-neutral-500 mt-2">Wave Type: <span className="font-medium text-neutral-700">{surfData.wave_type}</span></p>
          </div>

          {/* Conditions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Swell */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <h4 className="font-semibold text-neutral-800 mb-3 flex items-center gap-2">
                <FaWater className="text-cyan-500" />
                Swell
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-neutral-500">Height</span>
                  <span className="font-medium">{surfData.swell_height_m}m ({surfData.swell_height_ft}ft)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Period</span>
                  <span className="font-medium">{surfData.swell_period_s}s</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Direction</span>
                  <span className="font-medium">{surfData.swell_direction}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Quality</span>
                  <span className={`font-medium ${
                    surfData.swell_quality === 'Epic' ? 'text-emerald-600' :
                    surfData.swell_quality === 'Good' ? 'text-green-600' :
                    surfData.swell_quality === 'Fair' ? 'text-yellow-600' : 'text-red-600'
                  }`}>{surfData.swell_quality}</span>
                </div>
              </div>
            </div>

            {/* Wind */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <h4 className="font-semibold text-neutral-800 mb-3 flex items-center gap-2">
                <FaWind className="text-gray-500" />
                Wind
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-neutral-500">Speed</span>
                  <span className="font-medium">{surfData.wind_speed_kmh} km/h ({surfData.wind_speed_mph} mph)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Direction</span>
                  <span className="font-medium">{surfData.wind_direction}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Type</span>
                  <span className={`font-medium ${
                    surfData.wind_type.includes('Offshore') || surfData.wind_type.includes('Glass') ? 'text-emerald-600' :
                    surfData.wind_type.includes('Onshore') ? 'text-red-600' : 'text-yellow-600'
                  }`}>{surfData.wind_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Impact</span>
                  <span className={`font-medium ${
                    surfData.wind_impact === 'Positive' ? 'text-emerald-600' :
                    surfData.wind_impact === 'Negative' ? 'text-red-600' : 'text-yellow-600'
                  }`}>{surfData.wind_impact}</span>
                </div>
              </div>
            </div>

            {/* Tide & Timing */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <h4 className="font-semibold text-neutral-800 mb-3 flex items-center gap-2">
                <FaCompass className="text-indigo-500" />
                Tide & Timing
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-neutral-500">Tide Status</span>
                  <span className="font-medium">{surfData.tide_status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Recommendation</span>
                  <span className="font-medium text-sm">{surfData.tide_recommendation}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Best Time</span>
                  <span className="font-medium text-sm">{surfData.best_time_today}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Crowd</span>
                  <span className="font-medium">{surfData.crowd_factor}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Skill Level Suitability */}
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
            <h4 className="font-semibold text-neutral-800 mb-4">Who Should Paddle Out?</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className={`text-center p-4 rounded-xl ${surfData.beginner_friendly ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="text-2xl mb-2">
                  {surfData.beginner_friendly ? <FaCheckCircle className="text-green-500 mx-auto" /> : <FaTimesCircle className="text-red-500 mx-auto" />}
                </div>
                <p className="font-medium text-neutral-800">Beginners</p>
                <p className="text-sm text-neutral-500">{surfData.beginner_friendly ? 'Go for it!' : 'Not today'}</p>
              </div>
              <div className={`text-center p-4 rounded-xl ${surfData.intermediate_suitable ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="text-2xl mb-2">
                  {surfData.intermediate_suitable ? <FaCheckCircle className="text-green-500 mx-auto" /> : <FaTimesCircle className="text-red-500 mx-auto" />}
                </div>
                <p className="font-medium text-neutral-800">Intermediate</p>
                <p className="text-sm text-neutral-500">{surfData.intermediate_suitable ? 'Good session' : 'Take caution'}</p>
              </div>
              <div className={`text-center p-4 rounded-xl ${surfData.advanced_conditions ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="text-2xl mb-2">
                  {surfData.advanced_conditions ? <FaCheckCircle className="text-green-500 mx-auto" /> : <FaQuestion className="text-yellow-500 mx-auto" />}
                </div>
                <p className="font-medium text-neutral-800">Advanced</p>
                <p className="text-sm text-neutral-500">{surfData.advanced_conditions ? 'Send it!' : 'Mellow day'}</p>
              </div>
            </div>
          </div>

          {/* Pros, Cons, Tips */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Pros */}
            <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100">
              <h4 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                <FaThumbsUp className="text-emerald-500" />
                Pros
              </h4>
              <ul className="space-y-2">
                {surfData.pros.map((pro, idx) => (
                  <li key={idx} className="text-emerald-800 text-sm flex items-start gap-2">
                    <span className="text-emerald-500 mt-1">+</span>
                    {pro}
                  </li>
                ))}
              </ul>
            </div>

            {/* Cons */}
            <div className="bg-red-50 rounded-2xl p-5 border border-red-100">
              <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                <FaThumbsDown className="text-red-500" />
                Cons
              </h4>
              <ul className="space-y-2">
                {surfData.cons.map((con, idx) => (
                  <li key={idx} className="text-red-800 text-sm flex items-start gap-2">
                    <span className="text-red-500 mt-1">-</span>
                    {con}
                  </li>
                ))}
              </ul>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <FaInfoCircle className="text-blue-500" />
                Tips
              </h4>
              <ul className="space-y-2">
                {surfData.tips.map((tip, idx) => (
                  <li key={idx} className="text-blue-800 text-sm flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="bg-teal-50 rounded-2xl p-6 flex items-start gap-4">
        <FaInfoCircle className="text-teal-500 text-xl flex-shrink-0 mt-1" />
        <div>
          <h4 className="font-semibold text-teal-900 mb-1">About Swell Decoding</h4>
          <p className="text-sm text-teal-700">
            This tool analyzes swell height, period, direction, and wind conditions to give you
            a simple surf forecast. Longer swell periods (12s+) generally mean better wave quality.
            Offshore winds clean up the waves, while onshore winds create choppy conditions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SurfingSwellDecoder;
